/**
 * Article presentation → CSS custom properties (article template G2, spec §6.1, decisions D3/D4).
 * Pure (no React runtime, no DOM): the same output in SSR and in the client re-render.
 *
 * Defence in depth (D3): apply and publish validate presentations, but a document can reach the
 * renderer without them (Studio save, direct Firestore write, an old draft). sanitizeArticlePresentation
 * drops every name that is not [a-z0-9-]+ and every value that could end a declaration, a rule or the
 * <style> element, or open a comment: ; { } < > \ /* *\/ — and every role whose font-family stack, colour,
 * letter-spacing or dimension value is not the G1 validator's own grammar for that field (fix round 1:
 * UNSAFE_VALUE alone let quotes and "(" through, e.g. a colour of `#fff"` or a dimension of `url(evil)`,
 * which can open a CSS string the emitted <style> block never closes).
 */
import type { CSSProperties } from 'react';
import type {
  ArticleDimensionValue,
  ArticlePresentation,
  ArticleSpacingValue,
  ArticleTextMetrics,
  ArticleTextMetricsOverride,
  ArticleTextRole,
} from '@segmently/schema-models/v2';

export const ARTICLE_SCOPE = '[data-content-kind="article"]';
export const DEFAULT_PHONE_BREAKPOINT = 600;
export const DEFAULT_GRID_BREAKPOINT = 900;

const SAFE_NAME = /^[a-z0-9-]+$/;
const UNSAFE_VALUE = /[;{}<>\\]|\/\*|\*\//;
const MAX_VALUE_LENGTH = 256;
const TEXT_TRANSFORMS = new Set(['none', 'uppercase']);
/**
 * Font-family stack grammar (M4) — the SAME regex as the G1 validator's FONT_FAMILY_ITEM
 * (screen-utils/article-template/articleDocumentValidator.ts:97): each comma-separated item is a balanced
 * "…" or '…' string or an unquoted identifier sequence (one optional leading hyphen, "-apple-system").
 * Mirrored, not imported: the web-shell bundle has no @modules/screen-utils alias. The parity rows in
 * articlePresentationCss.test.ts fail if the two grammars ever disagree.
 */
const FONT_FAMILY_ITEM = /^(?:"[^"\\\n\r]*"|'[^'\\\n\r]*'|-?[A-Za-z][\w -]*)$/;
/**
 * Colour / letter-spacing / dimension grammars (fix round 1) — the SAME regexes as the G1 validator's
 * NUMBER / HSL_TOKEN / HSL_ARGS / CSS_COLOR / LETTER_SPACING / DIMENSION_VALUE
 * (screen-utils/article-template/articleDocumentValidator.ts:67,77-80,99-100). UNSAFE_VALUE alone (no
 * `; { } < > \` or a comment) is not enough: it lets through `"`, `'` and `(`, so a palette colour of
 * `#fff"`, a dimension of `0px'` or `url(https://evil)` would open (or extend) a CSS string the emitted
 * <style> block never closes, corrupting or dropping every declaration/rule after it. Mirrored, not
 * imported (same reason as FONT_FAMILY_ITEM): the parity rows in articlePresentationCss.test.ts fail if
 * the two grammars ever disagree. MAX_CSS_VALUE mirrors the validator's own length cap for these fields
 * (family keeps MAX_VALUE_LENGTH — the validator's separate, larger MAX_FONT_STACK).
 */
const MAX_CSS_VALUE = 64;
const NUMBER = '-?(?:\\d+(?:\\.\\d+)?|\\.\\d+)';
const HSL_TOKEN = `${NUMBER}(?:deg|grad|rad|turn|%)?`;
const HSL_ARGS = `${HSL_TOKEN}(?:[\\s,/]+${HSL_TOKEN})*`;
const CSS_COLOR = new RegExp(
  `^(#[0-9a-f]{3,4}|#[0-9a-f]{6}|#[0-9a-f]{8}|rgba?\\([0-9.,%\\s/]+\\)|hsla?\\(${HSL_ARGS}\\)|transparent|currentcolor)$`,
  'i',
);
const LETTER_SPACING = new RegExp(`^(?:normal|${NUMBER}(?:px|em|rem)?)$`);
const DIMENSION_VALUE = new RegExp(`^${NUMBER}(?:px|%|em|rem|vw|vh)?$`);

type Metrics = Partial<ArticleTextMetrics>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Own-property lookup: role/token names never resolve to Object.prototype members ("toString"). */
export function hasOwn(record: object | undefined, key: string): boolean {
  return record !== undefined && Object.prototype.hasOwnProperty.call(record, key);
}

export function isValidFontFamilyStack(value: string): boolean {
  return value.split(',').every((item) => FONT_FAMILY_ITEM.test(item.trim()));
}

function safeString(value: unknown, maxLength: number = MAX_VALUE_LENGTH): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed && trimmed.length <= maxLength && !UNSAFE_VALUE.test(trimmed) ? trimmed : undefined;
}

/** A palette colour: the G1 validator's CSS_COLOR grammar, not just "no unsafe characters". */
function safeColor(value: unknown): string | undefined {
  const trimmed = safeString(value, MAX_CSS_VALUE);
  return trimmed !== undefined && CSS_COLOR.test(trimmed) ? trimmed : undefined;
}

/** A dimension/letter-spacing value: the G1 validator's DIMENSION_VALUE grammar. */
function safeCssLength(value: unknown): string | undefined {
  const trimmed = safeString(value, MAX_CSS_VALUE);
  return trimmed !== undefined && DIMENSION_VALUE.test(trimmed) ? trimmed : undefined;
}

/** A letter-spacing value: the G1 validator's LETTER_SPACING grammar ("normal" or a CSS length). */
function safeLetterSpacing(value: unknown): string | undefined {
  const trimmed = safeString(value, MAX_CSS_VALUE);
  return trimmed !== undefined && LETTER_SPACING.test(trimmed) ? trimmed : undefined;
}

const positive = (value: unknown): number | undefined =>
  (typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined);
const nonNegative = (value: unknown): number | undefined =>
  (typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined);

function safeMap<T>(raw: unknown, pick: (value: unknown) => T | undefined): Record<string, T> | undefined {
  if (!isRecord(raw)) return undefined;
  const out: Record<string, T> = {};
  for (const [name, value] of Object.entries(raw)) {
    if (!SAFE_NAME.test(name)) continue;
    const picked = pick(value);
    if (picked !== undefined) out[name] = picked;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function safeMetrics(raw: unknown): Metrics | undefined {
  if (!isRecord(raw)) return undefined;
  const metrics: Metrics = {};
  const size = positive(raw.size);
  if (size !== undefined) metrics.size = size;
  const lineHeight = positive(raw.lineHeight);
  if (lineHeight !== undefined) metrics.lineHeight = lineHeight;
  const weight = positive(raw.weight);
  if (weight !== undefined) metrics.weight = weight;
  const letterSpacing = safeLetterSpacing(raw.letterSpacing);
  if (letterSpacing !== undefined) metrics.letterSpacing = letterSpacing;
  if (typeof raw.textTransform === 'string' && TEXT_TRANSFORMS.has(raw.textTransform)) {
    metrics.textTransform = raw.textTransform as ArticleTextMetrics['textTransform'];
  }
  return Object.keys(metrics).length > 0 ? metrics : undefined;
}

function safeRole(raw: unknown): ArticleTextRole | undefined {
  if (!isRecord(raw)) return undefined;
  const family = safeString(raw.family);
  const desktop = safeMetrics(raw.desktop);
  if (!family || !isValidFontFamilyStack(family)) return undefined;
  if (!desktop || desktop.size === undefined || desktop.lineHeight === undefined || desktop.weight === undefined) return undefined;
  const phone = safeMetrics(raw.phone);
  const color = typeof raw.color === 'string' && SAFE_NAME.test(raw.color) ? raw.color : undefined;
  return {
    family,
    desktop: desktop as ArticleTextMetrics,
    ...(phone ? { phone: phone as ArticleTextMetricsOverride } : {}),
    ...(color ? { color } : {}),
  };
}

function safeSpacing(raw: unknown): ArticleSpacingValue | undefined {
  if (!isRecord(raw)) return undefined;
  const desktop = nonNegative(raw.desktop);
  if (desktop === undefined) return undefined;
  const phone = nonNegative(raw.phone);
  return phone === undefined ? { desktop } : { desktop, phone };
}

function safeDimension(raw: unknown): ArticleDimensionValue | undefined {
  if (!isRecord(raw)) return undefined;
  const desktop = safeCssLength(raw.desktop);
  if (!desktop) return undefined;
  const phone = safeCssLength(raw.phone);
  const dark = safeCssLength(raw.dark);
  return { desktop, ...(phone ? { phone } : {}), ...(dark ? { dark } : {}) };
}

/** The token maps the renderer uses; null when none survives (legacy {version, profiles} → no style block). */
export function sanitizeArticlePresentation(raw: unknown): ArticlePresentation | null {
  if (!isRecord(raw)) return null;
  const palette = isRecord(raw.palette) ? raw.palette : {};
  const light = safeMap(palette.light, safeColor);
  const dark = safeMap(palette.dark, safeColor);
  const roles = safeMap(raw.roles, safeRole);
  const spacing = safeMap(raw.spacing, safeSpacing);
  const dimensions = safeMap(raw.dimensions, safeDimension);
  if (!light && !dark && !roles && !spacing && !dimensions) return null;
  const breakpoints = isRecord(raw.breakpoints) ? raw.breakpoints : {};
  const phone = positive(breakpoints.phone);
  const grid = positive(breakpoints.grid);
  return {
    ...(light || dark ? { palette: { ...(light ? { light } : {}), ...(dark ? { dark } : {}) } } : {}),
    ...(roles ? { roles } : {}),
    ...(spacing ? { spacing } : {}),
    ...(dimensions ? { dimensions } : {}),
    ...(phone !== undefined || grid !== undefined
      ? { breakpoints: { ...(phone !== undefined ? { phone } : {}), ...(grid !== undefined ? { grid } : {}) } }
      : {}),
  };
}

/** Phone layout applies BELOW breakpoints.phone (schema ArticleBreakpoints): max-width phone − 0.02px. */
export function phoneMaxWidth(p: ArticlePresentation): number {
  return (p.breakpoints?.phone ?? DEFAULT_PHONE_BREAKPOINT) - 0.02;
}

export function gridMaxWidth(p: ArticlePresentation): number {
  return (p.breakpoints?.grid ?? DEFAULT_GRID_BREAKPOINT) - 0.02;
}

function sortedEntries<T>(record: Record<string, T> | undefined): Array<[string, T]> {
  return Object.keys(record ?? {}).sort().map((key) => [key, (record as Record<string, T>)[key]]);
}

function roleMetricVars(name: string, metrics: Metrics): string[] {
  const out: string[] = [];
  if (metrics.size !== undefined) out.push(`--ap-role-${name}-size:${metrics.size}px`);
  if (metrics.lineHeight !== undefined) out.push(`--ap-role-${name}-lh:${metrics.lineHeight}`);
  if (metrics.weight !== undefined) out.push(`--ap-role-${name}-weight:${metrics.weight}`);
  if (metrics.letterSpacing !== undefined) out.push(`--ap-role-${name}-ls:${metrics.letterSpacing}`);
  if (metrics.textTransform !== undefined) out.push(`--ap-role-${name}-tt:${metrics.textTransform}`);
  return out;
}

/** Desktop values, then the phone media block, then the dark media block (D3). Names are sorted. */
export function buildArticleVariablesCss(p: ArticlePresentation): string {
  const desktop: string[] = [];
  const phone: string[] = [];
  const dark: string[] = [];
  for (const [name, value] of sortedEntries(p.palette?.light)) desktop.push(`--ap-color-${name}:${value}`);
  for (const [name, value] of sortedEntries(p.palette?.dark)) dark.push(`--ap-color-${name}:${value}`);
  for (const [name, role] of sortedEntries(p.roles)) {
    desktop.push(`--ap-role-${name}-family:${role.family}`, ...roleMetricVars(name, role.desktop));
    if (role.color) desktop.push(`--ap-role-${name}-color:var(--ap-color-${role.color})`);
    if (role.phone) phone.push(...roleMetricVars(name, role.phone));
  }
  for (const [name, value] of sortedEntries(p.spacing)) {
    desktop.push(`--ap-space-${name}:${value.desktop}px`);
    if (value.phone !== undefined) phone.push(`--ap-space-${name}:${value.phone}px`);
  }
  for (const [name, value] of sortedEntries(p.dimensions)) {
    desktop.push(`--ap-dim-${name}:${value.desktop}`);
    if (value.phone !== undefined) phone.push(`--ap-dim-${name}:${value.phone}`);
    if (value.dark !== undefined) dark.push(`--ap-dim-${name}:${value.dark}`);
  }
  let css = desktop.length > 0 ? `${ARTICLE_SCOPE}{${desktop.join(';')}}` : '';
  if (phone.length > 0) css += `@media (max-width:${phoneMaxWidth(p)}px){${ARTICLE_SCOPE}{${phone.join(';')}}}`;
  if (dark.length > 0) css += `@media (prefers-color-scheme:dark){${ARTICLE_SCOPE}{${dark.join(';')}}}`;
  return css;
}

function roleDefines(role: ArticleTextRole, key: keyof ArticleTextMetrics): boolean {
  return role.desktop[key] !== undefined || role.phone?.[key] !== undefined;
}

/** Inline style for a text role (D4): applied UNDER the label's literal appearance. */
export function articleRoleStyle(p: ArticlePresentation | null | undefined, roleName: string | undefined): CSSProperties | undefined {
  if (!p?.roles || !roleName || !hasOwn(p.roles, roleName)) return undefined;
  const role = p.roles[roleName];
  const v = (suffix: string) => `var(--ap-role-${roleName}-${suffix})`;
  const style: CSSProperties = {
    fontFamily: v('family'),
    fontSize: v('size'),
    lineHeight: v('lh'),
    fontWeight: v('weight') as CSSProperties['fontWeight'],
  };
  if (roleDefines(role, 'letterSpacing')) style.letterSpacing = v('ls');
  if (roleDefines(role, 'textTransform')) style.textTransform = v('tt') as CSSProperties['textTransform'];
  if (role.color) style.color = v('color');
  return style;
}

/** The same role as CSS declarations for style-block rules ('' for an unknown role). */
export function roleDeclarations(p: ArticlePresentation, roleName: string): string {
  const style = articleRoleStyle(p, roleName);
  if (!style) return '';
  const pairs: Array<[string, unknown]> = [
    ['font-family', style.fontFamily], ['font-size', style.fontSize], ['line-height', style.lineHeight],
    ['font-weight', style.fontWeight], ['letter-spacing', style.letterSpacing], ['text-transform', style.textTransform],
    ['color', style.color],
  ];
  return pairs.filter(([, value]) => value !== undefined).map(([property, value]) => `${property}:${String(value)};`).join('');
}
